import { ApiQueryParams } from '@frontend/api-utils';
import type { IGroup } from '@frontend/group/types';
import { SettingKey, SettingValue } from '@frontend/setting/types';
import { TransactionUserClient } from '@frontend/transaction/api';
import { TransactionUser, TransactionUserType } from '@frontend/transaction/types';
import { IUser, UserListResponse, UserQueryParams } from '@frontend/user/types';
import { User } from '@frontend/user/utils';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { resolveNestedUserGroupsByMemberOrder } from '../../utils/resolve-nested-user-groups-by-member-order';
import { resolveUsersByGroupMemberOrder } from '../../utils/resolve-users-by-group-member-order';
import { useScreen } from '../useScreen';

const toBooleanSetting = (value: SettingValue | undefined): boolean => value === true || value === 'true' || value === 1 || value === '1';

const DELAYED_RECEIVERS_MIN_SEARCH_CHARACTERS = 3;

export type SelectGroupedReceiversView = 'groups' | 'groupUsers' | 'allUsers';

const dedupeUsersById = (users: IUser[]): IUser[] => {
    const seen = new Set<string>();
    const result: IUser[] = [];
    for (const user of users) {
        if (seen.has(user.id)) continue;
        seen.add(user.id);
        result.push(user);
    }
    return result;
};

interface ViewProps {
    isLoading?: boolean;
    view: SelectGroupedReceiversView;
    groups: IGroup[];
    selectedGroup: IGroup | null;
    onSelectGroup: (group: IGroup) => void;
    selectedReceivers: (string | null)[];
    receiverSubsets: (string[] | undefined)[];
    addReceiver: () => void;
    removeReceiver: (index: number) => void;
    updateReceiver: (index: number, receiver: string | null) => void;
    delayedReceiversSearch: boolean;
    fetchReceiverOptions: (queryParams?: ApiQueryParams<UserQueryParams>) => Promise<UserListResponse>;
    showReceiverNotFoundToAllUsers: boolean;
    showReceiverNotFoundModal: boolean;
    onReceiverNotFoundToAllUsers: () => void;
    onReceiverNotFoundOpenModal: () => void;
    showNotFoundModal: boolean;
    notFoundName: string;
    setNotFoundName: (value: string) => void;
    closeNotFoundModal: () => void;
    onNotFoundModalSubmit: () => void;
    canContinue: boolean;
    showBack: boolean;
    onSubmit: () => void;
    onBack: () => void;
}

const useSelectGroupedReceivers = (): ViewProps => {
    const { store, onBack: navigateBack, onSubmit: onContinue, updateWizardData, backEnabled } = useScreen();
    const { transaction, syncTransaction } = useWizardTransaction({
        wizardId: store?.wizard.id,
        store,
        autoCreate: true
    });

    const [isLoading, setIsLoading] = useState(true);
    const [view, setView] = useState<SelectGroupedReceiversView>('groups');
    const [groups, setGroups] = useState<IGroup[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<IGroup | null>(null);
    const [receivers, setReceivers] = useState<IUser[] | null>(null);
    const [selectedReceivers, setSelectedReceivers] = useState<(string | null)[]>([null]);
    const selectedReceiversRef = useRef(selectedReceivers);

    const [showNotFoundModal, setShowNotFoundModal] = useState(false);
    const [notFoundName, setNotFoundName] = useState('');

    const storeReceivers = (store?.data?.receivers as TransactionUser[]) || [];
    const remoteReceiversRef = useRef<TransactionUser[]>([]);

    const delayedReceiversSearch = toBooleanSetting(store?.functionalitySettings?.find((fs) => fs.key === SettingKey.DELAYED_RECEIVERS_SEARCH)?.value);

    const defaultReceiverIdValue = store?.functionalitySettings?.find((fs) => fs.key === SettingKey.DEFAULT_RECEIVER_ID)?.value;
    const defaultReceiverId = typeof defaultReceiverIdValue === 'string' && defaultReceiverIdValue.trim() ? defaultReceiverIdValue.trim() : null;

    useEffect(() => {
        selectedReceiversRef.current = selectedReceivers;
    }, [selectedReceivers]);

    useEffect(() => {
        const receiverUserGroupId = store?.functionalitySettings?.find((fs) => fs.key === SettingKey.OPEN_DROP_OFF_RECEIVERS_USER_GROUP_ID)?.value;

        if (receiverUserGroupId && typeof receiverUserGroupId === 'string') {
            const getInfo = async () => {
                const nestedGroups = await resolveNestedUserGroupsByMemberOrder(receiverUserGroupId);
                setGroups(nestedGroups);

                if (transaction?.id) {
                    const fetchedReceivers = await TransactionUserClient.fetchTransactionUsers({
                        transaction_id: transaction.id,
                        type: TransactionUserType.RECEIVER
                    });
                    remoteReceiversRef.current = fetchedReceivers.results;

                    if (storeReceivers.length === 0 && fetchedReceivers.results.length > 0) {
                        updateWizardData({
                            ...store.data,
                            receivers: fetchedReceivers.results
                        });
                    }
                }
            };
            getInfo().finally(() => setIsLoading(false));
        } else {
            setIsLoading(false);
        }
    }, []);

    const writeReceivers = (receiverIds: (string | null)[]) => {
        if (!store) return;

        const validReceivers = receiverIds.filter((id): id is string => id !== null);
        const newReceivers: Partial<TransactionUser>[] = validReceivers.map((userId) => ({
            user_id: userId,
            type: TransactionUserType.RECEIVER,
            transaction_id: store.data?.transaction?.id || '',
            account_id: store.wizard.account_id,
            creation_timestamp: new Date().toISOString(),
            update_timestamp: new Date().toISOString()
        }));

        updateWizardData({
            ...store.data,
            receivers: newReceivers
        });
    };

    const clearReceivers = () => {
        const cleared: (string | null)[] = [null];
        selectedReceiversRef.current = cleared;
        setSelectedReceivers(cleared);
        writeReceivers([]);
    };

    const syncReceiversAndAdvance = async (receiverIds: string[]) => {
        if (!store || !transaction?.id) return;

        const accountId = store.wizard.account_id;
        const transactionId = transaction.id;
        const remoteReceivers = remoteReceiversRef.current;

        const receiversToAdd = receiverIds.filter((local) => !remoteReceivers.some((remote) => remote.user_id === local));
        const receiversToRemove = remoteReceivers.filter((remote) => !receiverIds.includes(remote.user_id));

        const addedReceivers: TransactionUser[] = [];
        for (const receiver of receiversToAdd) {
            const addedReceiver = await TransactionUserClient.postTransactionUser(accountId, transactionId, {
                user_id: receiver,
                type: TransactionUserType.RECEIVER
            });
            addedReceivers.push(addedReceiver);
        }

        for (const receiver of receiversToRemove) {
            await TransactionUserClient.deleteTransactionUser(accountId, transactionId, receiver.user_id);
        }

        remoteReceiversRef.current = [...addedReceivers, ...remoteReceivers.filter((remote) => receiverIds.includes(remote.user_id))];

        const nextStep = store.steps.find((step) => step.screen === ScreenType.SELECT_GROUPED_RECEIVERS && step.trigger === ScreenTrigger.SUBMIT);
        onContinue(nextStep);
    };

    const onSelectGroup = async (group: IGroup) => {
        setIsLoading(true);
        setSelectedGroup(group);
        setView('groupUsers');
        const currentStoreReceivers = (store?.data?.receivers as TransactionUser[]) || [];
        clearReceivers();
        try {
            const orderedReceivers = await resolveUsersByGroupMemberOrder(group.id);
            setReceivers(orderedReceivers);
            const foundReceivers = orderedReceivers.filter((user) => currentStoreReceivers.some((receiver) => receiver.user_id === user.id));
            if (foundReceivers.length > 0) {
                const ids = foundReceivers.map((user) => user.id);
                selectedReceiversRef.current = ids;
                setSelectedReceivers(ids);
                writeReceivers(ids);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const addReceiver = () => {
        const updated = [...selectedReceiversRef.current, null];
        selectedReceiversRef.current = updated;
        setSelectedReceivers(updated);
    };

    const removeReceiver = (index: number) => {
        const filtered = selectedReceiversRef.current.filter((_, i) => i !== index);
        const updated = filtered.length > 0 ? filtered : [null];
        selectedReceiversRef.current = updated;
        setSelectedReceivers(updated);
        writeReceivers(updated);
    };

    const updateReceiver = (index: number, receiver: string | null) => {
        const updated = [...selectedReceiversRef.current];
        updated[index] = receiver;
        selectedReceiversRef.current = updated;
        setSelectedReceivers(updated);
        writeReceivers(updated);
    };

    const receiverSubsets = useMemo(
        () =>
            selectedReceivers.map((receiverId) =>
                receivers
                    ?.filter((receiver) => {
                        const found = selectedReceivers.find((id) => id === receiver.id);
                        if (found) {
                            return receiverId === found;
                        }
                        return true;
                    })
                    .map((receiver) => receiver.id)
            ),
        [receivers, selectedReceivers]
    );

    const fetchReceiverOptions = useCallback(
        (queryParams?: ApiQueryParams<UserQueryParams>): Promise<UserListResponse> => {
            const search = typeof queryParams?.search === 'string' ? queryParams.search.trim().toLowerCase() : '';

            if (delayedReceiversSearch && search.length < DELAYED_RECEIVERS_MIN_SEARCH_CHARACTERS) {
                return Promise.resolve({ count: 0, results: [] });
            }

            let filtered = receivers ?? [];

            if (search) {
                filtered = filtered.filter((user) => {
                    const displayName = new User(user).getDisplayName().toLowerCase();
                    return displayName.includes(search) || user.unique_identifier.toLowerCase().includes(search);
                });
            }

            const index = Number.parseInt(String(queryParams?.index ?? '0'), 10);
            const size = Number.parseInt(String(queryParams?.size ?? '100'), 10);

            return Promise.resolve({
                count: filtered.length,
                results: filtered.slice(index * size, (index + 1) * size)
            });
        },
        [receivers, delayedReceiversSearch]
    );

    const onReceiverNotFoundToAllUsers = async () => {
        setIsLoading(true);
        setSelectedGroup(null);
        clearReceivers();
        setView('allUsers');
        try {
            const nestedResults = await Promise.all(groups.map((group) => resolveUsersByGroupMemberOrder(group.id)));
            setReceivers(dedupeUsersById(nestedResults.flat()));
        } finally {
            setIsLoading(false);
        }
    };

    const onReceiverNotFoundOpenModal = () => {
        if (!defaultReceiverId) return;
        setNotFoundName('');
        setShowNotFoundModal(true);
    };

    const closeNotFoundModal = () => {
        if (isLoading) return;
        setShowNotFoundModal(false);
    };

    const onNotFoundModalSubmit = async () => {
        const trimmed = notFoundName.trim();
        if (!trimmed || !defaultReceiverId || !transaction?.id) return;

        setIsLoading(true);
        try {
            const nextSelection = [defaultReceiverId];
            setSelectedReceivers(nextSelection);
            selectedReceiversRef.current = nextSelection;
            writeReceivers(nextSelection);

            const existingNotes = typeof transaction.shipping_notes === 'string' ? transaction.shipping_notes.trim() : '';
            const shippingNotes = existingNotes ? `${existingNotes} - ${trimmed}` : trimmed;
            await syncTransaction({ shipping_notes: shippingNotes });
            setShowNotFoundModal(false);
            await syncReceiversAndAdvance(nextSelection);
        } finally {
            setIsLoading(false);
        }
    };

    const canContinue = selectedReceivers.some((receiver) => receiver !== null);

    const onBack = () => {
        if (view === 'allUsers' || view === 'groupUsers') {
            setView('groups');
            setSelectedGroup(null);
            setReceivers(null);
            clearReceivers();
            return;
        }
        if (backEnabled) {
            navigateBack();
        }
    };

    const onSubmit = async () => {
        if (!store || !canContinue || !transaction?.id) return;

        const relevantReceivers = selectedReceiversRef.current.filter((receiver): receiver is string => receiver !== null);
        if (relevantReceivers.length === 0) return;

        setIsLoading(true);
        try {
            await syncReceiversAndAdvance(relevantReceivers);
        } finally {
            setIsLoading(false);
        }
    };

    return {
        isLoading,
        view,
        groups,
        selectedGroup,
        onSelectGroup,
        selectedReceivers,
        receiverSubsets,
        addReceiver,
        removeReceiver,
        updateReceiver,
        delayedReceiversSearch,
        fetchReceiverOptions,
        showReceiverNotFoundToAllUsers: view === 'groups',
        showReceiverNotFoundModal: (view === 'groupUsers' || view === 'allUsers') && !!defaultReceiverId && !canContinue,
        onReceiverNotFoundToAllUsers,
        onReceiverNotFoundOpenModal,
        showNotFoundModal,
        notFoundName,
        setNotFoundName,
        closeNotFoundModal,
        onNotFoundModalSubmit,
        canContinue,
        showBack: view !== 'groups' || backEnabled,
        onSubmit,
        onBack
    };
};

export default useSelectGroupedReceivers;
